<template>
  <div class="grid grid-cols-1 m-8 bg-white shadow-md p-4 gap-4">
    <div class="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
      <!-- Pet Info Section -->
      <div class="grid grid-cols-1 gap-4">
        <h2 class="col-span-1 text-lg flex items-center gap-2">
          <span class="border-black border-b-2">{{ t('petInfo') }}</span>
          <router-link :to="editUrl">
            <font-awesome-icon icon="edit" />
          </router-link>
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('petName') }}:</div>
          <div class="col-span-1">{{ pet.name || '-' }}</div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('type') }}:</div>
          <div class="col-span-1">{{ pet.type || '-' }}</div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('breed') }}:</div>
          <div class="col-span-1">{{ pet.breed || '-' }}</div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('color') }}:</div>
          <div class="col-span-1">{{ pet.color || '-' }}</div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('sex') }}:</div>
          <div class="col-span-1">{{ pet.sex ? t(pet.sex) : '-' }}</div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('dateOfBirth') }}:</div>
          <div class="col-span-1">{{ formattedDateOfBirth || '-' }}</div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold">{{ t('neutered') }}:</div>
          <div class="col-span-1">
            <el-tag :type="pet.neutered ? 'success' : 'info'" size="small">
              {{ pet.neutered ? t('yes') : t('no') }}
            </el-tag>
          </div>
        </div>
      </div>

      <!-- Owner Info Section -->
      <div class="self-start grid grid-cols-1 gap-4">
        <h2 class="col-span-1 text-lg flex items-center gap-2">
          <span class="border-black border-b-2">{{ t('ownerInfo') }}</span>
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold col-span-1">{{ t('ownerName') }}:</div>
          <div class="col-span-1">{{ pet.customerName || '-' }}</div>
        </div>

        <div v-if="pet.customer?.phone" class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold col-span-1">{{ t('phone') }}:</div>
          <div class="col-span-1" style="direction: ltr">{{ pet.customer.phone }}</div>
        </div>

        <div v-if="pet.customer?.email" class="grid grid-cols-1 md:grid-cols-2 gap-4 px-4">
          <div class="font-bold col-span-1">{{ t('email') }}:</div>
          <div class="col-span-1">{{ pet.customer.email }}</div>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="flex justify-end">
      <div class="md:w-2/12 m-2">
        <horizontal-button
          :title="t('newVetService')"
          @click.prevent="goToNewVetService"
          isSuccess
        />
      </div>
    </div>

    <!-- Vet Services Table -->
    <div class="col-span-full border-t-2 pt-2">
      <h2 class="col-span-1 md:col-span-2 text-lg flex items-center gap-2">
        <span class="border-black border-b-2">{{ t('vetServicesHistory') }}</span>
      </h2>
      <ag-grid-vue
        class="ag-theme-alpine mt-4"
        :columnDefs="columnDefs"
        :rowData="vetServiceRows"
        :gridOptions="gridOptions"
        rowSelection="multiple"
        :enable-rtl="$ctx.getDir() === 'rtl'"
        style="height: 40vh"
        :pagination="true"
        :paginationAutoPageSize="true"
        :defaultColDef="defaultColDef"
        @cell-double-clicked="onVetServiceDoubleClicked"
        :onGridReady="onGridReady"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { AgGridVue } from 'ag-grid-vue3'
import { ColDef, GridApi, GridOptions } from 'ag-grid-community'

import { useHTTP } from '@/plugins/http'
import { Route as $Route } from '@/router/routes'
import { formatPostgresDate } from '@/utils/date'

import Pet, { petI18nMessages } from '@/models/Pet'
import VetService, { vetServiceI18nMessages, VetServiceItem } from '@/models/VetService'
import HorizontalButton from '@/components/HorizontalButton.vue'
import Item from '@/models/inventory/Item'

// Flattened row interface - one row per service item
interface VetServiceRow {
  vetServiceId: string
  externalID: string | number | undefined
  createdAt: Date | string | undefined
  vetName: string
  serviceName: string
  servicePrice: number | undefined
  notes: string
}

const { t } = useI18n({
  messages: {
    en: {
      ...petI18nMessages.en,
      ...vetServiceI18nMessages.en,
      petInfo: 'Pet Information',
      ownerInfo: 'Owner Information',
      newVetService: 'New Service',
      vetServicesHistory: 'Vet Services History',
      date: 'Date',
      time: 'Time',
      vet: 'Vet',
      services: 'Services',
      notes: 'Notes',
      yes: 'Yes',
      no: 'No',
      phone: 'Phone #',
      email: 'Email'
    },
    ar: {
      ...petI18nMessages.ar,
      ...vetServiceI18nMessages.ar,
      petInfo: 'معلومات الأليف',
      ownerInfo: 'معلومات المالك',
      newVetService: 'خدمة جديدة',
      vetServicesHistory: 'سجل الخدمات البيطرية',
      date: 'التاريخ',
      time: 'الوقت',
      vet: 'الطبيب البيطري',
      services: 'الخدمات',
      notes: 'الملاحظات',
      yes: 'نعم',
      no: 'لا',
      phone: 'رقم الهاتف',
      email: 'البريد الالكتروني'
    }
  }
})

const gridOptions: GridOptions = {}

const columnDefs: ColDef[] = [
  {
    headerName: `${t('vetService')} #`,
    field: 'externalID',
    checkboxSelection: true,
    headerCheckboxSelection: true
  },
  {
    headerName: t('date'),
    field: 'createdAt',
    cellStyle: { textAlign: 'center' },
    valueGetter: (params) => {
      if (!params.data?.createdAt) return ''
      return formatPostgresDate(String(params.data.createdAt), 'DD/MM/YYYY')
    }
  },
  {
    headerName: t('time'),
    field: 'createdAt',
    cellStyle: { direction: 'ltr !important', textAlign: 'center' },
    valueGetter: (params) => {
      if (!params.data?.createdAt) return ''
      return formatPostgresDate(String(params.data.createdAt), 'hh:mm A')
    }
  },
  {
    headerName: t('service'),
    field: 'serviceName'
  },
  {
    headerName: t('vet'),
    field: 'vetName'
  }
]

const defaultColDef: ColDef = {
  sortable: true,
  filter: true,
  resizable: true,
  minWidth: 150
}

const pet = ref(new Pet())
const vetServiceRows = ref<VetServiceRow[]>([])
const $route = useRoute()
const $router = useRouter()
const $http = useHTTP()
const gridApi = ref<GridApi | undefined>()

const formattedDateOfBirth = computed(() => {
  if (!pet.value.dateOfBirth) return ''
  return formatPostgresDate(pet.value.dateOfBirth.toString(), 'DD-MM-YYYY')
})

const editUrl = computed(() => {
  const id = $route.params.id
  return $Route.PETS_PET.replace(':id', id as string)
})

function onGridReady(params: any) {
  gridApi.value = params.api
  gridApi.value?.sizeColumnsToFit()
}

async function loadPet() {
  const id = $route.params.id as string

  try {
    // Load pet data
    const petResp = await $http.get<Pet>(`${Pet.ENDPOINT}/${id}`)
    pet.value = Pet.from(petResp)

    // Load vet services for this pet
    const vetServicesResp = await $http.get<VetService[]>(
      `${VetService.ENDPOINT}?petID=${id}`
    )

    let vetServices: VetService[] = []
    if (Array.isArray(vetServicesResp)) {
      vetServices = vetServicesResp.map((vs: any) => VetService.from(vs))
    } else if (vetServicesResp && (vetServicesResp as any).data) {
      vetServices = (vetServicesResp as any).data.map((vs: any) => VetService.from(vs))
    }

    // Flatten vet services - create one row per service item
    const rows: VetServiceRow[] = []
    for (const vs of vetServices) {
      const services = vs.extraProperties?.services || []
      const items = vs.items || []

      if (services.length === 0) {
        // If no service items, still show the vet service record
        rows.push({
          vetServiceId: vs.id,
          externalID: vs.externalID,
          createdAt: vs.createdAt,
          vetName: vs.vetName,
          serviceName: '-',
          servicePrice: undefined,
          notes: vs.notes
        })
      } else {
        // Create a row for each service item
        for (const serviceItem of services) {
          const item = items.find((i: Item) => i.id === serviceItem.itemID)
          rows.push({
            vetServiceId: vs.id,
            externalID: vs.externalID,
            createdAt: vs.createdAt,
            vetName: vs.vetName,
            serviceName: item?.name || '-',
            servicePrice: serviceItem.price,
            notes: vs.notes
          })
        }
      }
    }

    vetServiceRows.value = rows
  } catch (error: any) {
    console.error('Failed to load pet:', error)
  }
}

onMounted(() => {
  loadPet()
})

function goToNewVetService() {
  $router.push({
    path: $Route.VET_SERVICES_NEW,
    query: { petId: $route.params.id as string }
  })
}

function onVetServiceDoubleClicked(event: any) {
  $router.push({
    name: 'vet-service',
    params: {
      id: event.data.vetServiceId
    }
  })
}
</script>

<style></style>
